import type { AddressFragment, CustomerFragment } from '#graphql-operations'
import type { CustomerDataLayerAddress, CustomerDataLayerPayload } from '../utils/customerDataLayer'
import { activeCustomer as $activeCustomer } from '@layers/common/app/services/auth'
import { defineStore } from 'pinia'
import {
  buildCustomerDataLayerPayload,
  clearCustomerSnapshot,
  readCustomerSnapshot,
  SIGNED_OUT_PAYLOAD,
  toDataLayerAddress,
  writeCustomerSnapshot,
} from '../utils/customerDataLayer'

export const useActiveCustomerStore = defineStore('activeCustomer', () => {
  const activeOrderStore = useActiveOrderStore()
  const nuxtApp = useNuxtApp()
  const { $gtm } = nuxtApp

  const activeCustomer = ref<CustomerFragment | null>(null)
  const dataLayerAddress = ref<CustomerDataLayerAddress | null>(null)
  const discardedOrderCustomer = shallowRef<CustomerFragment | null>(null)

  /**
   * False until the activeCustomer request has actually come back. A null
   * customer means "signed out" only once this is true - before that it merely
   * means the deferred check has not run yet.
   */
  const resolved = ref(false)
  let latest = 0

  const fetchActiveCustomer = async () => {
    const ticket = ++latest
    try {
      const customer = await $activeCustomer()
      if (ticket !== latest)
        return activeCustomer.value
      if (!customer && activeCustomer.value) {
        clearActiveCustomer()
      }
      else {
        activeCustomer.value = customer || null
        resolved.value = true
      }
      return activeCustomer.value
    }
    catch (error) {
      if (ticket !== latest)
        return activeCustomer.value
      throw error
    }
  }

  const currentCustomer = computed(() => {
    const orderCustomer = activeOrderStore.activeOrder.data?.customer
    return activeCustomer.value || (orderCustomer !== discardedOrderCustomer.value ? orderCustomer : null)
  })

  // Identical payloads are the common case - the restored snapshot usually
  // matches what the background check returns - and re-pushing one would make
  // every downstream tag fire a second time.
  let lastPushed: string | null = null
  function pushToDataLayer(payload: CustomerDataLayerPayload) {
    const signature = JSON.stringify(payload)
    if (signature === lastPushed)
      return

    lastPushed = signature
    $gtm?.push?.(payload)
  }

  /**
   * The whole point of the snapshot: the customer is in the dataLayer
   * synchronously, before `route_change` or `view_item` fire.
   *
   * Resolving the customer over the network cannot get here in time - the fetch
   * is deliberately deferred to an idle callback a second after mount so it
   * stays clear of LCP - and SSR is not an option, because the rendered HTML is
   * CDN-cached and has to stay identical for every visitor. Reading it back out
   * of storage costs no request at all.
   */
  const restored = import.meta.client ? readCustomerSnapshot() : null
  const restoredUserId = restored?.user_id ?? null
  if (restored) {
    if (restored.user_data?.address)
      dataLayerAddress.value = restored.user_data.address

    pushToDataLayer(restored)
  }

  let addressRequested = false
  let addressCustomerId = restoredUserId
  let addressGeneration = 0

  function clearActiveCustomer() {
    latest++
    addressGeneration++
    addressCustomerId = null
    addressRequested = false
    discardedOrderCustomer.value = activeOrderStore.activeOrder.data?.customer ?? null
    activeCustomer.value = null
    dataLayerAddress.value = null
    resolved.value = true
    clearCustomerSnapshot()
    pushToDataLayer(SIGNED_OUT_PAYLOAD)
  }

  watch([currentCustomer, resolved, dataLayerAddress], ([customer, isResolved]) => {
    if (import.meta.server)
      return

    // Before the deferred check, retain the restored address; after an identity change every
    // scheduled or in-flight lookup belongs to the previous customer and must be ignored.
    const userId = customer ? String(customer.id) : null
    if ((customer || isResolved) && userId !== addressCustomerId) {
      addressGeneration++
      addressCustomerId = userId
      addressRequested = false
      dataLayerAddress.value = null
    }

    if (!customer) {
      // Only a *resolved* absence is a sign-out. This is also the correction
      // path for a stale snapshot: storage is cleared and the variables reset,
      // so a lapsed session stops reporting the previous shopper.
      if (isResolved) {
        clearCustomerSnapshot()
        pushToDataLayer(SIGNED_OUT_PAYLOAD)
      }
      return
    }

    // The address is a second request, so it is kept off the path that
    // establishes identity and off the critical path altogether: once per customer,
    // when idle, and only when the snapshot did not already carry one. Every
    // later page in the tab reads it back out of storage for free.
    if (!addressRequested && !dataLayerAddress.value) {
      addressRequested = true
      const generation = addressGeneration
      // The idle callback runs outside the Nuxt context, where `useGraphqlQuery`
      // would throw - restore it explicitly.
      whenIdle(() => void nuxtApp.runWithContext(() => loadDefaultAddress(customer, generation)))
    }

    const payload = buildCustomerDataLayerPayload(customer, dataLayerAddress.value)
    writeCustomerSnapshot(payload)
    pushToDataLayer(payload)
  }, { immediate: true })

  async function loadDefaultAddress(customer: CustomerFragment, generation: number) {
    const isCurrent = () => generation === addressGeneration && String(currentCustomer.value?.id) === String(customer.id)
    if (!isCurrent())
      return
    try {
      const { data } = await useGraphqlQuery('activeCustomerAddresses')
      if (!isCurrent() || String(data.activeCustomer?.id) !== String(customer.id))
        return
      // `useGraphqlQuery` is loosely typed from inside this layer, so name the
      // element type rather than inferring it.
      const addresses: AddressFragment[] = data.activeCustomer?.addresses ?? []
      const preferred = addresses.find(address => address.defaultShippingAddress) ?? addresses[0]

      if (preferred)
        dataLayerAddress.value = toDataLayerAddress(customer, preferred)
    }
    catch (err) {
      // Analytics enrichment must never surface to the shopper; the payload
      // simply ships without an address.
      if (isCurrent())
        console.error('Failed to fetch customer address for dataLayer:', err)
    }
  }

  return {
    activeCustomer: computed(() => activeCustomer.value),
    resolved: computed(() => resolved.value),
    currentCustomer,
    fetchActiveCustomer,
    clearActiveCustomer,
    /**
     * One saved address for the signed-in customer, if we happen to have one.
     * Read by the product page to decide whether to mention Saturday delivery.
     *
     * Deliberately not called "default shipping address", which is what a reader
     * would otherwise assume. It is `defaultShippingAddress ?? addresses[0]`
     * (see `loadDefaultAddress`), so a customer who never flagged a default gets
     * whichever address is first — possibly a work or gift address in another
     * city. Good enough to decide what a page may *mention*, not good enough to
     * promise a date against.
     *
     * Client-only, and best-effort in a second sense: the whole point is that it
     * costs nothing, because the address is already being fetched for the
     * dataLayer. On the first page of a tab that fetch has not landed yet (it
     * waits for `refreshActiveCustomer`, then for an idle callback), so this is
     * null for the first few seconds and then fills in. Every later page in the
     * tab reads it back out of the sessionStorage snapshot synchronously. It is
     * `null` throughout SSR, by design — the rendered HTML is CDN-cached and
     * shared by every visitor, so nothing per-customer may reach it.
     *
     * Carries `country` as a *name*, not a code (`toDataLayerAddress`), so it
     * cannot be used for a reliable country test.
     */
    knownAddress: computed(() => dataLayerAddress.value),
  }
})

/** Runs `task` when the browser is next idle, falling back where unsupported. */
function whenIdle(task: () => void) {
  if (typeof requestIdleCallback === 'undefined') {
    setTimeout(task, 1000)
    return
  }

  requestIdleCallback(task, { timeout: 5000 })
}
